import {useQuery, useQueryClient} from 'react-query'
import axios from 'axios'
import {useAuth} from '../app/modules/auth'

export interface Claim {
  id: string
  memberName: string
  cardNumber: string
  policyNumber: string
  customerGroup: string
  status: string
  claimDate: string
  claimType: string
  treatmentType: string
  claimStatus: string
  claimNo: string
  customerName: string
}

export interface Page {
  currentPage: number
  pageSize: number
  totalItems: number
  totalPages: number
}

export interface ClaimsResponse {
  isSuccess: boolean
  data: Claim[]
  page: Page
  totalRecords?: number
}

export interface ClaimsRequestParams {
  searchType: string
  searchQuery: string
  currentPage: number
  pageSize: number
  activeTab: 'all' | 'myturn'
}

// Fetch claims function
const fetchClaims = async (
  params: ClaimsRequestParams,
  token: string,
  providerCode: string,
  email: string
): Promise<ClaimsResponse> => {
  const {searchType, searchQuery, currentPage, pageSize, activeTab} = params

  // Build query parameters
  const queryParts: string[] = []
  const queryParam = queryParts.join('&')

  const apiUrl =
    activeTab === 'myturn'
      ? `${process.env.REACT_APP_API_CLAIM_URL}/api/Provider/ListClaimProviderTask?providerCode=${providerCode}`
      : `${process.env.REACT_APP_API_CLAIM_URL}/api/Provider/ListClaimProvider?providerCode=${providerCode}`

  // Initialize requestBody with base values
  const requestBody: any = {
    email: email,
    corporateName: '',
    memberName: '',
    cardNumber: '',
    claimNumber: '',
    policyNumber: '',
    treatmentType: '',
    status: '',
    page: {
      page: currentPage,
      pageSize: pageSize,
      q: queryParam || '',
      type: 'email',
    },
  }

  // Apply trimmed query to the appropriate field based on searchType
  if (searchQuery && searchQuery.trim()) {
    const trimmedQuery = searchQuery.trim()
    switch (searchType) {
      case 'Claim Number':
        requestBody.claimNumber = trimmedQuery
        break
      case 'Nama Member':
        requestBody.memberName = trimmedQuery
        break
      case 'Card Number':
        requestBody.cardNumber = trimmedQuery
        break
      case 'Policy Number':
        requestBody.policyNumber = trimmedQuery
        break
      case 'Treatment Type':
        requestBody.treatmentType = trimmedQuery
        break
      case 'Customer Group':
        requestBody.corporateName = trimmedQuery
        break
      case 'Created Date':
        requestBody.specificClaimDate = trimmedQuery
        break
      case 'Status':
        requestBody.status = trimmedQuery
        break
      default:
        break
    }
  }

  const response = await axios.post(apiUrl, requestBody, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  console.log('response', response)

  return response.data
}

// Fetch My Turn total records function
const fetchMyTurnTotal = async (
  token: string,
  providerCode: string,
  email: string
): Promise<number> => {
  const response = await axios.post(
    `${process.env.REACT_APP_API_CLAIM_URL}/api/Provider/ListClaimProviderTask?providerCode=${providerCode}`,
    {
      email: email,
      page: {
        page: 1,
        pageSize: 100,
        q: '',
        type: 'email',
      },
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (response.data?.isSuccess) {
    return response.data.data.length || response.data.page.totalItems || 0
  }
  return 0
}

// Main hook for fetching claims
export const useClaims = (params: ClaimsRequestParams) => {
  const {auth, currentUser} = useAuth()
  const queryClient = useQueryClient()

  // Use auth object for token and provider info, currentUser for email
  const token = auth?.token || currentUser?.token
  const providerCode = auth?.provider?.providerCode || currentUser?.providerCode
  const email = currentUser?.email || auth?.userEmail

  // Debug logging
  console.log('useClaims - enabled:', !!token && !!providerCode && !!email)

  return useQuery(
    ['claims', params, providerCode, token], // Include token in query key to ensure fresh data
    () => {
      console.log('useClaims - fetchClaims called with:', {params, token, providerCode, email})
      if (!token || !providerCode || !email) {
        throw new Error('Missing authentication data')
      }
      return fetchClaims(params, token, providerCode, email)
    },
    {
      enabled: !!token && !!providerCode && !!email,
      staleTime: 0, // Always consider data stale for immediate refetch
      cacheTime: 0, // No cache - always fetch fresh data
      retry: (failureCount, error: any) => {
        console.log('useClaims - retry attempt:', failureCount, error)
        // Don't retry on 401 errors
        if (error?.response?.status === 401) {
          return false
        }
        return failureCount < 3
      },
      onError: (error: any) => {
        console.error('Error fetching claims:', error)
        if (error?.response?.status === 401) {
          // Handle token expiration
          queryClient.clear()
        }
      },
      onSuccess: (data: any) => {
        console.log('useClaims - success:', data)
      },
      // Add refetch on window focus for better UX
      refetchOnWindowFocus: false,
      // Don't keep previous data - always show fresh data
      keepPreviousData: false,
    }
  )
}

// Hook for fetching My Turn total records
export const useMyTurnTotal = () => {
  const {auth, currentUser} = useAuth()

  // Use auth object for token and provider info, currentUser for email
  const token = auth?.token || currentUser?.token
  const providerCode = auth?.provider?.providerCode || currentUser?.providerCode
  const email = currentUser?.email || auth?.userEmail

  // Debug logging
  console.log('useMyTurnTotal - enabled:', !!token && !!providerCode && !!email)

  return useQuery(
    ['myTurnTotal', providerCode, token], // Include token in query key
    () => {
      console.log('useMyTurnTotal - fetchMyTurnTotal called with:', {token, providerCode, email})
      if (!token || !providerCode || !email) {
        throw new Error('Missing authentication data')
      }
      return fetchMyTurnTotal(token, providerCode, email)
    },
    {
      enabled: !!token && !!providerCode && !!email,
      staleTime: 0, // Always consider data stale for immediate refetch
      cacheTime: 0, // No cache - always fetch fresh data
      retry: (failureCount, error: any) => {
        console.log('useMyTurnTotal - retry attempt:', failureCount, error)
        if (error?.response?.status === 401) {
          return false
        }
        return failureCount < 3
      },
      onError: (error: any) => {
        console.error('Error fetching my turn total:', error)
      },
      onSuccess: (data: any) => {
        console.log('useMyTurnTotal - success:', data)
      },
    }
  )
}

// Fetch claim details function
export const fetchClaimDetails = async (claimNo: string, token: string): Promise<any> => {
  const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Provider/GetByClaimNumber/${claimNo}`

  const response = await axios.get(apiUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return response.data
}

// Hook for fetching claim details by claim number
export const useClaimDetails = (claimNo: string | null, autoFetch: boolean = false) => {
  const {auth, currentUser} = useAuth()

  // Use auth object for token
  const token = auth?.token || currentUser?.token

  return useQuery(
    ['claimDetails', claimNo, token],
    () => {
      if (!claimNo || !token) {
        throw new Error('Missing claim number or token')
      }
      return fetchClaimDetails(claimNo, token)
    },
    {
      enabled: autoFetch && !!claimNo && !!token, // Enable automatic fetching when autoFetch is true
      staleTime: 0, // Always consider data stale for immediate refetch
      cacheTime: 0, // No cache - always fetch fresh data
      retry: (failureCount, error: any) => {
        // Don't retry on 401 errors
        if (error?.response?.status === 401) {
          return false
        }
        return failureCount < 3
      },
      onError: (error: any) => {
        console.error('Error fetching claim details:', error)
      },
    }
  )
}

// Hook for on-demand claim details fetching
export const useClaimDetailsFetcher = () => {
  const {auth, currentUser} = useAuth()
  const queryClient = useQueryClient()

  // Use auth object for token
  const token = auth?.token || currentUser?.token

  const fetchClaimDetailsOnDemand = async (claimNo: string) => {
    if (!token) {
      throw new Error('Missing authentication token')
    }

    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['claimDetails', claimNo, token],
        queryFn: () => fetchClaimDetails(claimNo, token),
        staleTime: 0, // Always consider data stale for immediate refetch
        cacheTime: 0, // No cache - always fetch fresh data
      })
      return data
    } catch (error) {
      console.error('Error fetching claim details:', error)
      throw error
    }
  }

  return {fetchClaimDetailsOnDemand}
}

// Hook for invalidating claims queries
export const useInvalidateClaims = () => {
  const queryClient = useQueryClient()

  const invalidateClaims = () => {
    queryClient.invalidateQueries(['claims'])
  }

  const invalidateMyTurnTotal = () => {
    queryClient.invalidateQueries(['myTurnTotal'])
  }

  const invalidateClaimDetails = (claimNo?: string) => {
    if (claimNo) {
      queryClient.invalidateQueries(['claimDetails', claimNo])
    } else {
      queryClient.invalidateQueries(['claimDetails'])
    }
  }

  const invalidateAll = () => {
    queryClient.invalidateQueries(['claims'])
    queryClient.invalidateQueries(['myTurnTotal'])
    queryClient.invalidateQueries(['claimDetails'])
  }

  return {
    invalidateClaims,
    invalidateMyTurnTotal,
    invalidateClaimDetails,
    invalidateAll,
  }
}
